/* eslint-disable @typescript-eslint/no-explicit-any */

import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { v4 as uuidv4 } from 'uuid'
import { Skeleton } from '@radix-ui/themes'

interface IDataGridProps {
  columns: any
  data: any
  loading: boolean
  classNames?: string
  sortColumn?: string | undefined
  sortOrder?: string | undefined
  sortFunction?: (column: string) => void
  lastItemClassName?: string
  rowClass?: string
  headerClass?: string
  maxWidth?: string
}

function DataGrid(props: IDataGridProps) {
  const {
    columns,
    data,
    loading,
    sortFunction,
    sortColumn,
    sortOrder,
    lastItemClassName = '',
    classNames = 'max-h-[calc(100vh_-_240px)]',
    rowClass = '!bg-tableRowGrey',
    headerClass = 'bg-tableHeaderGrey',
    maxWidth = '300px',
  } = props

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <>
      {data && !loading && (
        <div className={`${classNames} overflow-y-auto border border-mainBorder border-collapse rounded-lg`}>
          <table className="w-full">
            <thead
              className={`h-[56px] border-mainBorder border-b ${headerClass}`}
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
              }}
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={uuidv4()} className="text-left h-[56px]">
                  {headerGroup.headers.map((header, idx) => (
                    <th
                      key={uuidv4()}
                      className={`font-normal text-sm w-[20px]  ${headerGroup.headers[0] ? 'pl-4' : 'pl-2'} ${header.column.getCanSort() && sortFunction ? 'cursor-pointer' : ''}`}
                      onClick={() => {
                        header.column.getCanSort() && sortFunction ? sortFunction(header.column.id) : null
                      }}
                    >
                      <div className={`${idx === headerGroup.headers.length - 1 ? lastItemClassName : ''} items-center flex`}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                        <span className="p-1">
                          {header.column.id === sortColumn ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={uuidv4()} className={`h-14 border-mainBorder border-b last:border-b-0 ${rowClass}`}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={uuidv4()}
                      className={`text-sm ${row.getVisibleCells()[0] ? 'pl-4' : 'pl-2'}`}
                      style={{
                        maxWidth: maxWidth ? maxWidth : '300px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        paddingRight: '10px',
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(!data || loading) && (
        <div className="flex justify-center items-center mt-20">
          <Skeleton className="h-[300px] w-full flex-grow" />
        </div>
      )}
    </>
  )
}

export default DataGrid
